import React, { useMemo, useState } from 'react';
import { Autocomplete, Box, Button, IconButton, Stack, TextField, Typography } from '@mui/material';
import { EditIcon } from 'src/icons/EditIcon';
import { DeleteIcon } from 'src/icons/DeleteIcon';
import {
    useDeleteUserManufacturerMutation,
    useGetAllManufacturerDiscountForProjectQuery,
    useGetManufacturerRecommendationForProjectQuery,
    useUpdateUserManufacturerMutation,
} from 'src/api/v1/manufacturer-discount-ApiSlice';
import { DeleteManufacturerDialog } from 'src/components/ManufacturerDialogs/DeleteManufacturerDialog';
import { RenameManufacturerDialog } from 'src/components/ManufacturerDialogs/RenameManufacturerDialog';
import { useAppDispatch } from 'src/app/hooks';
import { showSnackBar } from 'src/api/ui-slice';
import { getV1ErrorMessage } from 'src/utils/v1ErrorMessage';
import { ts, ts2 } from 'src/utils/translate';

export type ProfileManufacturer = {
    internal_product_manufacturer_id?: number;
    user_manufacturer_id?: number;
    manufacturer_name: string;
};

type NewManufacturerOption = { inputValue: string };
type Option = ProfileManufacturer | NewManufacturerOption;

export const manufacturerKey = (manufacturer: ProfileManufacturer) => manufacturer.internal_product_manufacturer_id != null
    ? `ipmi_${manufacturer.internal_product_manufacturer_id}`
    : `umi_${manufacturer.user_manufacturer_id}`;

const toManufacturer = (raw: ProfileManufacturer): ProfileManufacturer => raw.internal_product_manufacturer_id != null
    ? { internal_product_manufacturer_id: raw.internal_product_manufacturer_id, manufacturer_name: raw.manufacturer_name }
    : { user_manufacturer_id: raw.user_manufacturer_id, manufacturer_name: raw.manufacturer_name };

const byName = (a: ProfileManufacturer, b: ProfileManufacturer) => a.manufacturer_name.localeCompare(b.manufacturer_name);

type Props = {
    projectId: number;
    discountedKeys: Set<string>;
    disabled: boolean;
    onSelect: (manufacturer: ProfileManufacturer) => void;
    onCreate: (manufacturerName: string) => void;
};

export const ProfileManufacturerAutocomplete = ({ projectId, discountedKeys, disabled, onSelect, onCreate }: Props) => {
    const dispatch = useAppDispatch();
    const { data: recommendations } = useGetManufacturerRecommendationForProjectQuery(projectId);
    const { data: projectDiscounts } = useGetAllManufacturerDiscountForProjectQuery(projectId);
    const [updateUserManufacturer, { isLoading: isRenameLoading }] = useUpdateUserManufacturerMutation();
    const [deleteUserManufacturer, { isLoading: isDeleteLoading }] = useDeleteUserManufacturerMutation();

    const [editedManufacturer, setEditedManufacturer] = useState<ProfileManufacturer | null>(null);
    const [editedName, setEditedName] = useState('');
    const [deletedManufacturer, setDeletedManufacturer] = useState<ProfileManufacturer | null>(null);

    // the recommendation leaves out what the project already discounts, so the two lists together give every manufacturer
    const manufacturers = useMemo(() => {
        if (!recommendations || !projectDiscounts) return [];

        const unique = new Map<string, ProfileManufacturer>();
        [
            ...Object.values(recommendations),
            ...(projectDiscounts.internal_manufacturer_discount ?? []),
            ...(projectDiscounts.user_manufacturer_discount ?? []),
        ].forEach((raw: ProfileManufacturer) => {
            const manufacturer = toManufacturer(raw);
            unique.set(manufacturerKey(manufacturer), manufacturer);
        });

        const all = Array.from(unique.values());
        const internals = all.filter(manufacturer => manufacturer.internal_product_manufacturer_id != null).sort(byName);
        const customs = all.filter(manufacturer => manufacturer.user_manufacturer_id != null).sort(byName);
        return [...internals, ...customs];
    }, [recommendations, projectDiscounts]);

    // custom manufacturers stay listed even with a discount, the list is the only place to rename or delete them
    const options = manufacturers.filter(manufacturer =>
        manufacturer.user_manufacturer_id != null || !discountedKeys.has(manufacturerKey(manufacturer))
    );

    const handleSelect = (manufacturer: ProfileManufacturer) => {
        if (discountedKeys.has(manufacturerKey(manufacturer))) {
            dispatch(showSnackBar({
                text: ts2('profile_settings_manufacturer_already_discounted', 'Manufacturer {manufacturer} already has a discount.', {
                    manufacturer: manufacturer.manufacturer_name,
                }),
                severity: 'error',
            }));
            return;
        }
        onSelect(manufacturer);
    };

    const showRequestError = (error: unknown) => {
        dispatch(showSnackBar({
            text: getV1ErrorMessage(error, ts('something_went_wrong_try_again', 'Something went wrong, please try again.')),
            severity: 'error',
        }));
    };

    const handleEditConfirm = () => {
        if (editedManufacturer?.user_manufacturer_id == null || editedName.trim() === '') {
            return;
        }
        updateUserManufacturer({ user_manufacturer_id: editedManufacturer.user_manufacturer_id, manufacturer_name: editedName.trim() })
            .unwrap()
            .then(() => setEditedManufacturer(null))
            .catch(showRequestError);
    };

    const handleDeleteConfirm = () => {
        if (deletedManufacturer?.user_manufacturer_id == null) {
            return;
        }
        deleteUserManufacturer({ user_manufacturer_id: deletedManufacturer.user_manufacturer_id })
            .unwrap()
            .then(() => setDeletedManufacturer(null))
            .catch(showRequestError);
    };

    return (
        <>
            <Autocomplete<Option>
                size="small"
                fullWidth
                openOnFocus
                disabled={disabled}
                options={options}
                value={null}
                getOptionLabel={(option) => 'inputValue' in option ? option.inputValue : option.manufacturer_name}
                noOptionsText={ts('q_typ_nores', 'No results found')}
                filterOptions={(allOptions, state) => {
                    const query: string = state.inputValue.trim().toLowerCase();
                    const filtered: Option[] = allOptions.filter(option =>
                        'manufacturer_name' in option && option.manufacturer_name.toLowerCase().includes(query)
                    );
                    const hasExactMatch = manufacturers.some(manufacturer => manufacturer.manufacturer_name.toLowerCase() === query);
                    if (query !== '' && !hasExactMatch) {
                        filtered.push({ inputValue: state.inputValue.trim() });
                    }
                    return filtered;
                }}
                renderOption={(props, option) => {
                    const { key, ...rest } = props;
                    if ('inputValue' in option) {
                        return (
                            <Box key={key} component="li" {...rest}>
                                <Button variant="contained" fullWidth sx={{ textTransform: 'none' }}>
                                    {ts2('profile_settings_add_new_manufacturer', 'Add {manufacturer} as new manufacturer', { manufacturer: option.inputValue })}
                                </Button>
                            </Box>
                        );
                    }
                    return (
                        <Stack key={key} component="li" direction="row" alignItems="center" {...rest}>
                            <Typography variant="body2" noWrap sx={{ flex: 1, minWidth: 0 }}>{option.manufacturer_name}</Typography>
                            {option.user_manufacturer_id != null && (
                                <Stack direction="row" spacing={0.5}>
                                    <IconButton
                                        size="small"
                                        onClick={(event) => {
                                            event.stopPropagation();
                                            setEditedName(option.manufacturer_name);
                                            setEditedManufacturer(option);
                                        }}
                                    >
                                        <EditIcon fontSize="small" />
                                    </IconButton>
                                    <IconButton
                                        size="small"
                                        onClick={(event) => {
                                            event.stopPropagation();
                                            setDeletedManufacturer(option);
                                        }}
                                    >
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                </Stack>
                            )}
                        </Stack>
                    );
                }}
                renderInput={(params) => <TextField {...params} autoFocus />}
                onChange={(_event, option) => {
                    if (!option) return;
                    if ('inputValue' in option) {
                        onCreate(option.inputValue);
                    } else {
                        handleSelect(option);
                    }
                }}
            />
            <RenameManufacturerDialog
                open={editedManufacturer !== null}
                manufacturerName={editedManufacturer?.manufacturer_name ?? ''}
                editedName={editedName}
                disabled={isRenameLoading}
                onChange={setEditedName}
                onClose={() => setEditedManufacturer(null)}
                onConfirm={handleEditConfirm}
            />
            <DeleteManufacturerDialog
                open={deletedManufacturer !== null}
                manufacturerName={deletedManufacturer?.manufacturer_name ?? ''}
                disabled={isDeleteLoading}
                onClose={() => setDeletedManufacturer(null)}
                onConfirm={handleDeleteConfirm}
            />
        </>
    );
};
